/**
 * Datalabels anchor position on the data element.
 * @see https://chartjs-plugin-datalabels.netlify.app/guide/positioning.html#anchoring
 */
export type BdChartDatalabelsAnchor = 'start' | 'center' | 'end';

/**
 * Datalabels alignment relative to the anchor point.
 * @see https://chartjs-plugin-datalabels.netlify.app/guide/positioning.html#alignment-and-offset
 */
export type BdChartDatalabelsAlign =
  | 'start'
  | 'center'
  | 'end'
  | 'left'
  | 'right'
  | 'top'
  | 'bottom';

/**
 * Number formatting options for datalabels values.
 * When set, a formatter using Intl.NumberFormat('de-DE') is applied.
 * When absent, any existing formatter on the chart config is preserved.
 */
export interface BdChartDatalabelsFormatting {
  minimumFractionDigits: number;
  maximumFractionDigits: number;
}

export const BD_CHART_DATALABELS_FORMATTING_DEFAULT: BdChartDatalabelsFormatting = {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
};

/**
 * Configuration options for Chart.js datalabels plugin.
 * @see https://chartjs-plugin-datalabels.netlify.app/guide/options.html
 */
export interface BdChartDatalabelsOptions {
  /**
   * Controls basic visibility of the labels.
   */
  display: boolean;

  /**
   * Anchor point on the data element.
   * @default 'center'
   */
  anchor?: BdChartDatalabelsAnchor;

  /**
   * Alignment relative to the anchor point.
   * @default 'center'
   */
  align?: BdChartDatalabelsAlign;

  /**
   * Distance from the anchor point in pixels.
   * @default 4
   */
  offset?: number;

  /**
   * Text color as hex string (e.g., '#ffffff').
   */
  color?: string;

  /**
   * Background color of the labels as hex string.
   */
  backgroundColor?: string;

  /**
   * Border color as hex string.
   */
  borderColor?: string;

  /**
   * Border width in pixels.
   * @default 0
   */
  borderWidth?: number;

  /**
   * Border radius for rounded corners.
   * @default 0
   */
  borderRadius?: number;

  /**
   * Inner padding in pixels.
   * @default 4
   */
  padding?: number;

  /**
   * Number formatting for datalabel values.
   * When set, overrides any existing formatter with Intl.NumberFormat.
   * When absent, the chart's existing formatter is preserved (e.g. cube-feature
   * uses dataset-specific formatting from operating number config).
   */
  formatting?: BdChartDatalabelsFormatting;
}

/**
 * Default datalabels options when enabled.
 */
export const BD_CHART_DATALABELS_OPTIONS_DEFAULT: BdChartDatalabelsOptions = {
  display: false,
  anchor: 'center',
  align: 'center',
  offset: 4,
  color: '#000000',
  borderWidth: 0,
  borderRadius: 0,
  padding: 4,
};
